import { useEffect, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Banknote } from "lucide-react";
import { toast } from "sonner";

export default function AdminCodSettings() {
  const [cfg, setCfg] = useState({ enabled: true, min_order_value: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/config/cod")
      .then((r) => setCfg({ enabled: r.data.enabled !== false, min_order_value: r.data.min_order_value || 0 }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/config/cod", { enabled: cfg.enabled, min_order_value: Number(cfg.min_order_value) || 0 });
      toast.success("COD settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save COD settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;
  return (
    <div data-testid="admin-cod-settings" className="mt-6 p-5 bg-white border border-gray-200 rounded-2xl max-w-xl">
      <div className="flex items-center gap-2 mb-1">
        <Banknote className="h-4 w-4 text-[#F48E2E]" />
        <div className="font-medium text-sm">Cash on Delivery (COD)</div>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Master switch and minimum order value for COD. Vendors can additionally turn COD off for their own products.
      </p>
      <div className="flex items-center justify-between py-2 border-t border-gray-100">
        <div>
          <div className="text-sm">Enable COD</div>
          <div className="text-[11px] text-gray-500">When off, COD is hidden at checkout for all buyers.</div>
        </div>
        <Switch data-testid="cod-enabled-toggle" checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} />
      </div>
      <div className="py-2 border-t border-gray-100">
        <Label className="text-xs">Minimum order value for COD (₹)</Label>
        <Input
          data-testid="cod-min-order"
          type="number"
          min="0"
          value={cfg.min_order_value}
          onChange={(e) => setCfg({ ...cfg, min_order_value: e.target.value })}
          className="h-9 w-44 mt-1"
        />
        <div className="text-[11px] text-gray-500 mt-1">Orders below this amount won't see COD at checkout. Set 0 for no minimum.</div>
      </div>
      <Button data-testid="cod-settings-save" onClick={save} disabled={saving} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815] mt-2 h-9">
        {saving ? "Saving…" : "Save COD settings"}
      </Button>
    </div>
  );
}
